import StatusChip from 'components/@extended/StatusChip';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import { TabsContent } from 'components/ui/tabs';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth';
import MyAllTimeRecords from 'pages/dashboard/MyAllTimeRecords';
import MyBDEfforts from 'pages/dashboard/MyBDEfforts';
import MyDraftTimecards, { myDraftTimecardsFilter } from 'pages/dashboard/MyDraftTimecards';
import MyFinalizedTimecards from 'pages/dashboard/MyFinalizedTimecards';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetPaginatedTimecardsWithFiltersQuery } from 'store/api/timecards';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import HomeSection, { HOME_LINK_CLASS, HOME_TAB_CLASS, HOME_TAB_LIST_CLASS } from './HomeSection';

type RecordsTab = 'drafts' | 'finalised' | 'bd-efforts' | 'all-time';

/** The full list behind each tab, linked from the header; All time has none. */
const TAB_LINK: Partial<Record<RecordsTab, { to: string; labelId: string }>> = {
    drafts: { to: '/timecards/draft', labelId: 'home.all-drafts' },
    finalised: { to: '/timecards/main', labelId: 'timecards.filter.my-timecards' },
    'bd-efforts': { to: '/efforts', labelId: 'dashboard.my-bd-efforts' },
};

/** A list scrolls inside the card once it outgrows it. */
const PANEL_CLASS = 'min-h-0 overflow-x-hidden overflow-y-auto';

/** The dashboard's four time lists, as tabs with Drafts first. */
export default function MyTimeRecordsSection() {
    const { user } = useAuth();
    const userId = user?.id;
    const [tab, setTab] = useState<RecordsTab>('drafts');
    const canSeeOwnTimecards = useHasPermissions(['READ.TIMECARDS.MY_OWN']);
    const canSeeOwnEfforts = useHasPermissions(['READ.EFFORTS.MY_OWN']);
    // The Drafts list is fetched inside its panel, which unmounts with its tab, so the label counts on its own.
    const { data: drafts } = useGetPaginatedTimecardsWithFiltersQuery(
        { page: 0, pageSize: 1, filters: myDraftTimecardsFilter(userId) },
        { skip: !userId }
    );
    const link = TAB_LINK[tab];

    return (
        <LineTabs value={tab} onValueChange={(value: RecordsTab) => setTab(value)} className="min-h-0 flex-1">
            <HomeSection
                title={t('home.my-time-records')}
                actions={
                    link ? (
                        <Link to={link.to} className={HOME_LINK_CLASS}>
                            {t(link.labelId)}
                        </Link>
                    ) : null
                }
                className="gap-1"
            >
                <LineTabsList className={HOME_TAB_LIST_CLASS}>
                    <LineTab
                        value="drafts"
                        size="compact"
                        className={HOME_TAB_CLASS}
                        icon={drafts ? <StatusChip size="small" type="warning" label={drafts.totalElements} /> : null}
                    >
                        {t('home.drafts')}
                    </LineTab>
                    <LineTab value="finalised" size="compact" className={HOME_TAB_CLASS}>
                        {t('home.finalised')}
                    </LineTab>
                    <LineTab value="bd-efforts" size="compact" className={HOME_TAB_CLASS}>
                        {t('menu.efforts')}
                    </LineTab>
                    {canSeeOwnTimecards && canSeeOwnEfforts ? (
                        <Tooltip content={t('dashboard.tooltips.my-all-time')}>
                            <LineTab value="all-time" size="compact" className={HOME_TAB_CLASS}>
                                {t('dashboard.my-all-time')}
                            </LineTab>
                        </Tooltip>
                    ) : null}
                </LineTabsList>
                {/* Each panel clips the dashboard's rows, which keep a minimum width wider than a one-column card. */}
                <TabsContent value="drafts" className={PANEL_CLASS}>
                    <MyDraftTimecards home />
                </TabsContent>
                {/* The other lists keep the dashboard's rows, which the card holds edge to edge. */}
                <TabsContent value="finalised" className={cn(PANEL_CLASS, '-mx-5')}>
                    <MyFinalizedTimecards />
                </TabsContent>
                <TabsContent value="bd-efforts" className={cn(PANEL_CLASS, '-mx-5')}>
                    <MyBDEfforts />
                </TabsContent>
                <TabsContent value="all-time" className={cn(PANEL_CLASS, '-mx-5')}>
                    <MyAllTimeRecords />
                </TabsContent>
            </HomeSection>
        </LineTabs>
    );
}
